import { useEffect, useState } from "react";

interface DynamicSVGProps {
  className: string;
  style: any,
  onClick?: () => void;
  svgPath: string;
  // Allow passing attributes for specific classes
  classAttributes?: Record<string, Record<string, string | number | boolean>>;
  [key: string]: unknown; // Use unknown for the index signature
}

const DynamicSVG = ({
  className,
  style = {},
  onClick,
  svgPath,
  classAttributes = {},
  ...svgProps
}: DynamicSVGProps) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);

  useEffect(() => {
    fetch(svgPath)
      .then(async (res) => {
        if (!res.ok) {
          // If response is not ok (including 404), return null
          setSvgContent(null);
          return null;
        }
        const text = await res.text();
        return text;
      })
      .then((data) => {
        if (data) {
          setSvgContent(data);
        }
      })
      .catch((err) => {
        console.log("error is in this file", svgPath);
        setSvgContent(null);
        console.error(err);
      });
  }, [svgPath]);

  if (!svgContent) return null;

  // Apply any SVG props to the SVG content
  let modifiedContent = svgContent;

  // Find the first SVG element
  const svgMatch = svgContent.match(/<svg[^>]*>/);
  if (svgMatch) {
    let svgTag = svgMatch[0];

    const hasWidth = /width="[^"]*"/.test(svgTag);
    const hasHeight = /height="[^"]*"/.test(svgTag);

    if (!hasWidth) {
      svgTag = svgTag.replace(/<svg/, `<svg width="100%"`);
    }

    if (!hasHeight) {
      svgTag = svgTag.replace(/<svg/, `<svg height="100%"`);
    }

    // Add any additional props to the SVG tag
    Object.entries(svgProps).forEach(([key, value]) => {
      if (value !== undefined) {
        // Convert camelCase to kebab-case for SVG attributes
        const kebabKey = key
          .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
          .toLowerCase();

        // Check if the attribute already exists
        const attrRegex = new RegExp(`${kebabKey}="[^"]*"`);
        if (svgTag.match(attrRegex)) {
          // Replace existing attribute
          svgTag = svgTag.replace(attrRegex, `${kebabKey}="${value}"`);
        } else {
          // Add new attribute
          svgTag = svgTag.replace(/<svg/, `<svg ${kebabKey}="${value}"`);
        }
      }
    });

    modifiedContent = svgContent.replace(svgMatch[0], svgTag);
  }

  // Apply attributes to elements with specific classes
  if (Object.keys(classAttributes).length > 0) {
    Object.entries(classAttributes).forEach(([className, attributes]) => {
      // Find elements with the specified class
      const classRegex = new RegExp(
        `class="[^"]*\\b${className}\\b[^"]*"`,
        "g"
      );
      const elementsWithClass = modifiedContent.match(classRegex);

      if (elementsWithClass) {
        elementsWithClass.forEach((elementClass) => {
          // Find the element tag that contains this class
          const elementTagRegex = new RegExp(
            `<[^>]*${elementClass}[^>]*>`,
            "g"
          );
          const elementTags = modifiedContent.match(elementTagRegex);

          if (elementTags) {
            elementTags.forEach((elementTag) => {
              let modifiedElementTag = elementTag;

              // Apply each attribute to the element
              Object.entries(attributes).forEach(([key, value]) => {
                if (value !== undefined) {
                  // Convert camelCase to kebab-case for SVG attributes
                  const kebabKey = key
                    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
                    .toLowerCase();

                  // Check if the attribute already exists
                  const attrRegex = new RegExp(`${kebabKey}="[^"]*"`);
                  if (modifiedElementTag.match(attrRegex)) {
                    // Replace existing attribute
                    modifiedElementTag = modifiedElementTag.replace(
                      attrRegex,
                      `${kebabKey}="${value}"`
                    );
                  } else {
                    // Add new attribute - be careful not to modify the tag name
                    const tagNameMatch =
                      modifiedElementTag.match(/<([a-z0-9]+)/i);
                    if (tagNameMatch) {
                      const tagName = tagNameMatch[1];
                      modifiedElementTag = modifiedElementTag.replace(
                        new RegExp(`<${tagName}`),
                        `<${tagName} ${kebabKey}="${value}"`
                      );
                    }
                  }
                }
              });

              // Replace the original element tag with the modified one
              modifiedContent = modifiedContent.replace(
                elementTag,
                modifiedElementTag
              );
            });
          }
        });
      }
    });
  }

  return (
    <div
      className={className}
      style={style}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: modifiedContent }}
    />
  );
};

export { DynamicSVG };
